import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft, Search, X } from "lucide-react";
import {
  ALL_ASSESSMENTS,
  letterOf,
  searchAssessments,
  sortedAssessments,
} from "@/data/learn/assessments";
import { GuestBanner } from "@/components/guest/GuestBanner";
import { useMembership } from "@/hooks/useMembership";
import { AssessmentCard } from "./learn.assessments.index";

export const Route = createFileRoute("/learn/assessments/a-z")({
  head: () => ({
    meta: [
      { title: "Self-Assessments A–Z — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Every Recovery Trauma™ self-assessment listed A–Z, from Anxiety Patterns to Trust After Trauma. Tap a letter to jump straight there. Educational reflection only.",
      },
      { property: "og:title", content: "Self-Assessments A–Z — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "An alphabetical directory of every Recovery Trauma™ self-assessment, with search and letter navigation.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/assessments/a-z" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/assessments/a-z" }],
  }),
  component: AssessmentsAZ,
});

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function AssessmentsAZ() {
  const { loading, isAuthed } = useMembership();
  const isGuest = !loading && !isAuthed;
  const [query, setQuery] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const searching = query.trim().length > 0;


  const matched = useMemo(() => sortedAssessments(searchAssessments(query)), [query]);

  const availableLetters = useMemo(
    () => new Set(matched.map((a) => letterOf(a))),
    [matched],
  );

  const shown = useMemo(
    () => (activeLetter ? matched.filter((a) => letterOf(a) === activeLetter) : matched),
    [matched, activeLetter],
  );

  const letters = useMemo(
    () => Array.from(new Set(shown.map((a) => letterOf(a)))).sort(),
    [shown],
  );

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
      <Link
        to="/learn/assessments"
        className="mb-5 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Self-Assessments
      </Link>
      {isGuest ? <GuestBanner /> : null}

      <header className="mb-6">

        <h1 className="font-serif text-3xl tracking-tight text-foreground md:text-4xl">
          Self-Assessments A–Z
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          All {ALL_ASSESSMENTS.length} self-assessments, listed alphabetically. Educational
          reflection tools only — they cannot diagnose any condition.
        </p>
      </header>

      <div className="relative mb-4">
        <Search
          className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveLetter(null);
          }}
          placeholder="Search self-assessments…"
          aria-label="Search self-assessments"
          className="w-full rounded-full border border-border bg-card py-3 ps-11 pe-11 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searching && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute end-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sticky alphabet — wraps on small screens, scrolls on larger ones */}
      <nav
        aria-label="Jump to letter"
        className="sticky top-0 z-10 -mx-4 mb-6 border-b border-border/60 bg-background/90 px-4 py-2 backdrop-blur sm:mb-8"
      >
        <div className="relative">
          <div className="flex flex-wrap gap-1 pb-1 sm:flex-nowrap sm:gap-1.5 sm:overflow-x-auto sm:[-webkit-overflow-scrolling:touch] sm:[scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              type="button"
              onClick={() => setActiveLetter(null)}
              aria-pressed={activeLetter === null}
              className={`inline-flex h-8 shrink-0 items-center rounded-full border px-2.5 text-[11px] font-semibold transition sm:h-9 sm:px-3 sm:text-xs ${
                activeLetter === null
                  ? "border-gold bg-gold text-white"
                  : "border-border bg-card text-foreground/75 hover:border-gold hover:text-gold"
              }`}
            >
              All
            </button>
            {ALPHABET.map((L) => {
              const enabled = availableLetters.has(L);
              const isActive = activeLetter === L;
              return (
                <button
                  key={L}
                  type="button"
                  disabled={!enabled}
                  aria-pressed={isActive}
                  aria-label={`Jump to ${L}`}
                  onClick={() => setActiveLetter(isActive ? null : L)}
                  className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition sm:h-9 sm:w-9 sm:text-sm ${
                    isActive
                      ? "border-gold bg-gold text-white shadow-sm"
                      : enabled
                        ? "border-border bg-card text-foreground/80 hover:border-gold hover:text-gold"
                        : "cursor-not-allowed border-transparent bg-muted/40 text-muted-foreground/40"
                  }`}
                >
                  {L}
                </button>
              );
            })}
          </div>
          <span
            className="pointer-events-none absolute inset-y-0 end-0 hidden w-6 bg-gradient-to-l from-background/90 to-transparent sm:block sm:w-8"
            aria-hidden="true"
          />
        </div>
      </nav>

      {shown.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          Nothing found{searching ? ` for “${query}”` : ""}. Try another word or letter.
        </p>
      ) : (
        <div className="space-y-6 sm:space-y-8">
          {letters.map((L) => (
            <section key={L} id={`letter-${L}`} aria-labelledby={`heading-${L}`} className="scroll-mt-24">
              <h2
                id={`heading-${L}`}
                className="mb-2 flex items-center gap-2 font-serif text-xl text-foreground sm:mb-3 sm:gap-3 sm:text-2xl"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-muted text-sm font-bold sm:h-10 sm:w-10 sm:rounded-2xl sm:text-base">
                  {L}
                </span>
                <span className="h-px flex-1 bg-border" />
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                {shown
                  .filter((a) => letterOf(a) === L)
                  .map((a, i) => (
                    <li key={a.slug}>
                      <AssessmentCard assessment={a} index={i} compact />
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </main>
  );
}
